// /admin — password-protected stats dashboard (Robert). Data from GET /api/admin/stats, which mirrors
// the 6 AM health email so the tab and the email always agree. No PII is shown here.
import { useEffect, useMemo, useState } from 'react';

const TABS = [
  ['overview', 'Overview'],
  ['yesterday', 'Completed yesterday'],
  ['unconverted', 'Signed, not converted'],
  ['nudges', 'Rep nudges'],
  ['almost', 'Almost done'],
];
const SERIES = { sent: '#2a78d6', opened: '#eb6834', completed: '#1baf7a' };

async function api(path, opts = {}) {
  const res = await fetch(`/api${path}`, { credentials: 'same-origin', headers: { 'Content-Type': 'application/json' }, ...opts });
  const body = await res.json().catch(() => ({}));
  if (!res.ok) { const e = new Error(body.message || body.error || `http_${res.status}`); e.status = res.status; throw e; }
  return body;
}
const fmtDate = (s) => s ? new Date(s).toLocaleString('en-US', { timeZone: 'America/Los_Angeles', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : '—';
const fmtDay = (s) => s ? new Date(s).toLocaleDateString('en-US', { timeZone: 'America/Los_Angeles', month: 'short', day: 'numeric' }) : '—';
const pct = (a, b) => b ? `${Math.round(100 * a / b)}%` : '—';

export function Admin() {
  const [user, setUser] = useState(undefined);
  useEffect(() => { api('/admin/me').then(r => setUser(r.user)).catch(() => setUser(null)); }, []);
  if (user === undefined) return <Shell><p className="text-slate-500">Loading…</p></Shell>;
  if (!user) return <Login onLogin={setUser} />;
  return <Dashboard user={user} onLogout={() => api('/admin/logout', { method: 'POST' }).then(() => setUser(null))} />;
}

function Shell({ children, right }) {
  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-brand-800 text-white">
        <div className="mx-auto max-w-7xl px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="font-semibold tracking-tight">Fundmerica Apply App</span>
            <span className="text-brand-100/80 text-sm">admin</span>
          </div>
          {right}
        </div>
      </header>
      <main className="mx-auto max-w-7xl px-4 py-6">{children}</main>
    </div>
  );
}

function Login({ onLogin }) {
  const [u, setU] = useState(''); const [p, setP] = useState(''); const [err, setErr] = useState(null); const [busy, setBusy] = useState(false);
  async function submit(e) {
    e.preventDefault(); setErr(null); setBusy(true);
    try { const r = await api('/admin/login', { method: 'POST', body: JSON.stringify({ username: u, password: p }) }); onLogin(r.user); }
    catch (e) { setErr(e.message); } finally { setBusy(false); }
  }
  return (
    <Shell>
      <form onSubmit={submit} className="card max-w-sm mx-auto mt-16 p-6 space-y-4">
        <h1 className="text-lg font-semibold">Sign in</h1>
        <div><label className="field-label">Username</label><input className="field-input" value={u} onChange={e => setU(e.target.value)} autoComplete="username" autoFocus /></div>
        <div><label className="field-label">Password</label><input className="field-input" type="password" value={p} onChange={e => setP(e.target.value)} autoComplete="current-password" /></div>
        {err && <p className="field-error">{err}</p>}
        <button className="btn-primary w-full" disabled={busy || !u || !p}>{busy ? 'Signing in…' : 'Sign in'}</button>
      </form>
    </Shell>
  );
}

function Dashboard({ user, onLogout }) {
  const [tab, setTab] = useState(() => (location.hash || '#overview').slice(1));
  const [data, setData] = useState(null); const [err, setErr] = useState(null); const [busy, setBusy] = useState(false);
  const load = (refresh) => { setBusy(true); setErr(null); return api(`/admin/stats${refresh ? '?refresh=1' : ''}`).then(setData).catch(e => setErr(e.message)).finally(() => setBusy(false)); };
  useEffect(() => { load(false); }, []);
  useEffect(() => { location.hash = tab; }, [tab]);
  const right = (
    <div className="flex items-center gap-3 text-sm">
      {data && <span className="text-brand-100/80 hidden sm:inline">as of {fmtDate(data.computed_at)} PT</span>}
      <button onClick={() => load(true)} disabled={busy} className="rounded-md bg-white/10 px-3 py-1 hover:bg-white/20 disabled:opacity-50">{busy ? 'Refreshing…' : 'Refresh'}</button>
      <button onClick={onLogout} className="rounded-md bg-white/10 px-3 py-1 hover:bg-white/20">Sign out ({user})</button>
    </div>
  );
  return (
    <Shell right={right}>
      <nav className="flex gap-1 overflow-x-auto border-b border-slate-200 mb-5">
        {TABS.map(([k, label]) => (
          <button key={k} onClick={() => setTab(k)} className={`whitespace-nowrap px-3 py-2 text-sm border-b-2 -mb-px ${tab === k ? 'border-brand-700 text-brand-800 font-medium' : 'border-transparent text-slate-500 hover:text-slate-800'}`}>
            {label}{data && <Count k={k} d={data} />}
          </button>
        ))}
      </nav>
      {err && <p className="text-red-600 mb-4">{err}</p>}
      {!data ? <p className="text-slate-500">{busy ? 'Loading stats (Salesforce lookups take a few seconds)…' : ''}</p> : (
        <>
          {tab === 'overview' && <Overview d={data} go={setTab} />}
          {tab === 'yesterday' && <Yesterday d={data} />}
          {tab === 'unconverted' && <Unconverted d={data} />}
          {tab === 'nudges' && <Nudges d={data} />}
          {tab === 'almost' && <Almost d={data} />}
        </>
      )}
    </Shell>
  );
}

function Count({ k, d }) {
  const n = k === 'yesterday' ? d.yesterday.unconverted.length : k === 'unconverted' ? d.unconverted.total : k === 'nudges' ? d.nudges.today.reduce((a, r) => a + r.leads, 0) : k === 'almost' ? d.almost_done.list.length : null;
  if (n == null) return null;
  return <span className="ml-1.5 rounded-full bg-slate-100 px-1.5 text-xs text-slate-600">{n}</span>;
}

// ── Overview ──
function Overview({ d, go }) {
  const y = d.yesterday, u = d.unconverted, n = d.nudges, s = d.sync;
  const today = d.funnel[d.funnel.length - 1];
  const tiles = [
    { label: `Completed ${y.label}`, value: y.total, sub: `${y.unconverted.length} not converted · ${y.converted.length} converted · ${y.existing_opp.length} on existing Opps`, tone: y.unconverted.length ? 'warn' : 'ok', tab: 'yesterday' },
    { label: 'Signed, not converted (60d)', value: u.total, sub: `${u.with_bank} with bank statements · ${u.never_nudged} never nudged`, tone: u.total ? 'warn' : 'ok', tab: 'unconverted' },
    { label: 'Nudges → resolved', value: `${n.totals.resolved} / ${n.totals.leads}`, sub: `${pct(n.totals.resolved, n.totals.leads)} of nudged leads resolved · ${n.totals.converted} converted · ${n.totals.open} still open`, tone: 'info', tab: 'nudges' },
    { label: 'Almost done (link live)', value: d.almost_done.list.length, sub: Object.entries(d.almost_done.counts).map(([k, v]) => `${v} ${k}`).join(' · ') || '—', tone: 'info', tab: 'almost' },
    { label: 'Today so far', value: `${today.sent} / ${today.opened} / ${today.completed}`, sub: 'sent / opened / completed', tone: 'info' },
    { label: 'Sync (24h)', value: s.failed ? `${s.failed} failed` : 'healthy', sub: `${s.success} jobs ok · ${s.stale_pending} stale · ${s.signed_stuck} stuck · last completion ${fmtDate(s.last_completed)}`, tone: s.failed || s.signed_stuck ? 'crit' : 'ok' },
  ];
  return (
    <div className="space-y-6">
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {tiles.map(t => (
          <button key={t.label} onClick={() => t.tab && go(t.tab)} className={`card p-4 text-left ${t.tab ? 'hover:ring-brand-500/50' : 'cursor-default'}`}>
            <div className="text-xs uppercase tracking-wide text-slate-500">{t.label}</div>
            <div className={`mt-1 text-2xl font-semibold ${t.tone === 'crit' ? 'text-red-700' : t.tone === 'warn' ? 'text-amber-700' : 'text-slate-900'}`}>{t.value}</div>
            <div className="mt-1 text-xs text-slate-500">{t.sub}</div>
          </button>
        ))}
      </div>
      <Funnel rows={d.funnel} />
      <div className="card p-4">
        <h3 className="font-medium mb-2">Signed-not-converted by rep</h3>
        <RepTable rows={u.by_rep} />
      </div>
    </div>
  );
}

function Funnel({ rows }) {
  const [hover, setHover] = useState(null);
  const W = 800, H = 160, padL = 36, padB = 22, n = rows.length, slot = (W - padL) / n;
  const chart = (keys, title, max) => (
    <div className="card p-4">
      <div className="flex items-center justify-between mb-1">
        <h3 className="font-medium">{title}</h3>
        <div className="flex gap-3 text-xs text-slate-600">{keys.map(k => <span key={k} className="flex items-center gap-1"><i className="inline-block h-2.5 w-2.5 rounded-sm" style={{ background: SERIES[k] }} />{k}</span>)}</div>
      </div>
      <div className="overflow-x-auto">
        <svg viewBox={`0 0 ${W} ${H}`} className="w-full min-w-[560px]" role="img" aria-label={title}>
          {[0, 0.5, 1].map(f => { const yy = H - padB - f * (H - padB - 10); return <g key={f}><line x1={padL} x2={W} y1={yy} y2={yy} stroke="#e2e8f0" /><text x={padL - 6} y={yy + 4} fontSize="10" textAnchor="end" fill="#64748b">{Math.round(f * max)}</text></g>; })}
          {rows.map((r, i) => {
            const bw = (slot - 8) / keys.length;
            return (
              <g key={r.day} onMouseEnter={() => setHover(i)} onMouseLeave={() => setHover(null)}>
                <rect x={padL + i * slot} y={0} width={slot} height={H} fill={hover === i ? '#f1f5f9' : 'transparent'} />
                {keys.map((k, j) => { const h = max ? (r[k] / max) * (H - padB - 10) : 0; const x = padL + i * slot + 4 + j * bw; return <rect key={k} x={x} y={H - padB - h} width={bw - 2} height={h} rx="2" fill={SERIES[k]} />; })}
                <text x={padL + i * slot + slot / 2} y={H - 6} fontSize="10" textAnchor="middle" fill={['Sat', 'Sun'].includes(r.dow) ? '#94a3b8' : '#475569'}>{r.dow} {r.day.slice(5)}</text>
              </g>
            );
          })}
        </svg>
      </div>
      <div className="h-5 text-xs text-slate-600">{hover != null && <>{rows[hover].day}: {keys.map(k => `${k} ${rows[hover][k]}`).join(' · ')}</>}</div>
    </div>
  );
  return (
    <div className="grid gap-4 lg:grid-cols-2">
      {chart(['sent'], 'Applications sent (14 days)', Math.max(1, ...rows.map(r => r.sent)))}
      {chart(['opened', 'completed'], 'Opened vs completed (14 days)', Math.max(1, ...rows.map(r => Math.max(r.opened, r.completed))))}
    </div>
  );
}

function RepTable({ rows }) {
  return (
    <Table cols={['Rep', 'Not converted', 'With bank stmts', 'Nudged', 'Nudged ×2', 'Never nudged', 'New (≤1d)', 'Oldest']}
      rows={rows.map(r => [r.rep, r.total, r.with_bank, r.nudged, r.nudged_twice, r.never_nudged, r.new_1d, `${r.oldest_days}d`])} />
  );
}

// ── Yesterday ──
function Yesterday({ d }) {
  const y = d.yesterday;
  return (
    <div className="space-y-6">
      <p className="text-sm text-slate-600"><b>{y.total}</b> applications completed {y.label} — <b className="text-amber-700">{y.unconverted.length} still on the Lead</b>, {y.converted.length} converted, {y.existing_opp.length} sent from an existing Opp (renewal — no conversion needed){y.missing.length ? `, ${y.missing.length} Lead not found` : ''}.</p>
      <Section title={`Not yet converted / submitted (${y.unconverted.length})`} tone="warn">
        <Table cols={['Rep', 'Business', 'Signed (PT)', 'Docs', 'Lead status', 'Sent by']} rows={y.unconverted.map(r => [r.rep, <Link href={r.url}>{r.business}</Link>, r.signed_pt, <Docs r={r} />, r.lead_status, r.sent_by || '—'])} />
      </Section>
      <Section title={`Converted (${y.converted.length})`}>
        <Table cols={['Rep', 'Business', 'Signed (PT)', 'Docs', 'Opp']} rows={y.converted.map(r => [r.rep, <Link href={r.url}>{r.business}</Link>, r.signed_pt, <Docs r={r} />, r.opp_url ? <Link href={r.opp_url}>open</Link> : '—'])} />
      </Section>
      <Section title={`Sent from an existing Opp (${y.existing_opp.length})`}>
        <Table cols={['Rep', 'Business', 'Signed (PT)', 'Docs', 'Stage']} rows={y.existing_opp.map(r => [r.rep, <Link href={r.url}>{r.business}</Link>, r.signed_pt, <Docs r={r} />, r.stage])} />
      </Section>
    </div>
  );
}

// ── Unconverted ──
function Unconverted({ d }) {
  const u = d.unconverted;
  const [rep, setRep] = useState(''); const [only, setOnly] = useState('all'); const [qs, setQs] = useState('');
  const rows = useMemo(() => u.leads.filter(r => (!rep || r.rep === rep) && (only === 'all' || (only === 'bank' && r.bank) || (only === 'never' && !r.nudges) || (only === 'nudged' && r.nudges)) && (!qs || r.business.toLowerCase().includes(qs.toLowerCase()))), [u, rep, only, qs]);
  return (
    <div className="space-y-4">
      <p className="text-sm text-slate-600"><b>{u.total}</b> merchants signed the application in the last 60 days and the Lead is still open — nothing submitted. <b>{u.with_bank}</b> have bank statements. <b>{u.never_nudged}</b> have never been Chatter-nudged (the daily job only covers new completions, so older ones stay here until someone acts).</p>
      <div className="flex flex-wrap gap-2 items-center text-sm">
        <select className="field-select w-auto" value={rep} onChange={e => setRep(e.target.value)}><option value="">All reps</option>{u.by_rep.map(r => <option key={r.rep} value={r.rep}>{r.rep} ({r.total})</option>)}</select>
        <select className="field-select w-auto" value={only} onChange={e => setOnly(e.target.value)}><option value="all">Everything</option><option value="bank">With bank statements</option><option value="never">Never nudged</option><option value="nudged">Nudged</option></select>
        <input className="field-input w-56" placeholder="Search business…" value={qs} onChange={e => setQs(e.target.value)} />
        <span className="text-slate-500">{rows.length} shown</span>
        <a className="btn-ghost ml-auto" href={`data:text/csv;charset=utf-8,${encodeURIComponent(toCsv(rows))}`} download={`apply_signed_not_converted_${new Date().toISOString().slice(0, 10)}.csv`}>Download CSV</a>
      </div>
      <Table cols={['Rep', 'Business', 'Signed', 'Age', 'Docs', 'Monthly sales', 'Requested', 'Nudges', 'Last nudge', 'Sent by']}
        rows={rows.map(r => [r.rep, <Link href={r.url}>{r.business}</Link>, fmtDay(r.completed), `${r.days}d`, <Docs r={r} />, r.gross_monthly_sales || '—', r.amount_requested || '—',
          r.nudges ? <Badge tone="ok">×{r.nudges}</Badge> : <Badge tone="warn">never</Badge>, fmtDay(r.last_nudged), r.sent_by || '—'])} />
    </div>
  );
}

// ── Nudges ──
function Nudges({ d }) {
  const n = d.nudges;
  return (
    <div className="space-y-6">
      <div className="grid gap-3 sm:grid-cols-4">
        <Stat label="Leads nudged (all time)" v={n.totals.leads} />
        <Stat label="Resolved after nudge" v={`${n.totals.resolved} (${pct(n.totals.resolved, n.totals.leads)})`} sub={`${n.totals.converted} converted`} />
        <Stat label="Still open" v={n.totals.open} />
        <Stat label="Chatter posts" v={n.totals.posts} />
      </div>
      <Section title={`Posted this morning (${n.today.reduce((a, r) => a + r.leads, 0)} leads in ${n.today.length} posts)`}>
        {n.today.length ? <Table cols={['Rep', 'Round', 'Leads', 'Posted', 'By', 'Post']} rows={n.today.map(r => [r.rep_name, r.round, r.leads, fmtDate(r.posted_at), r.posted_by, r.feed_item_id ? <Link href={`https://fundmerica.lightning.force.com/${r.feed_item_id}`}>open</Link> : '—'])} /> : <p className="text-sm text-slate-500">Nothing posted today (weekends are skipped; weekdays post at 6 AM PT).</p>}
      </Section>
      <div className="grid gap-4 lg:grid-cols-2">
        <Section title="By rep"><Table cols={['Rep', 'Leads nudged', 'Resolved', 'Converted', 'Last post']} rows={n.by_rep.map(r => [r.rep, r.leads, `${r.resolved} (${pct(r.resolved, r.leads)})`, r.converted, fmtDay(r.last_posted)])} /></Section>
        <Section title="By day (30d)"><Table cols={['Day', 'Posts', 'Leads', 'Resolved since']} rows={n.by_day.map(r => [r.day, r.posts, r.leads, r.resolved])} /></Section>
      </div>
      <Section title="Recent nudges (last 300)">
        <Table cols={['Posted', 'Rep', 'Business', 'Round', 'By', 'Outcome', 'Post']} rows={n.recent.map(r => [fmtDate(r.posted_at), r.rep_name, <Link href={r.url}>{r.business_name || r.lead_id}</Link>, r.round, r.posted_by,
          r.resolved_at ? <Badge tone="ok">{r.resolution} · {fmtDay(r.resolved_at)}</Badge> : <Badge tone="warn">open</Badge>, r.post_url ? <Link href={r.post_url}>open</Link> : '—'])} />
      </Section>
      <p className="text-xs text-slate-500">Policy: every weekday at 6 AM PT, one Chatter post per rep covering apps completed since the previous 6 AM run (Fri–Mon on Mondays) whose Lead is still unconverted — @rep + Jalonni Diggs + Melissa Petty, asking what happened. One notice per lead; no backlog sweep. Change via NUDGE_* env in healthcheck/healthcheck.env.</p>
    </div>
  );
}

// ── Almost done ──
function Almost({ d }) {
  const a = d.almost_done;
  return (
    <div className="space-y-4">
      <p className="text-sm text-slate-600">Links still live: {Object.entries(a.counts).map(([k, v]) => `${v} ${k}`).join(' · ') || 'none'}. Reps should use <b>Manage Application → Resend link</b> — sending a new Apply App wipes the merchant's progress.</p>
      <Table cols={['Status', 'Business', 'Fields', 'Docs', 'Owners signed', 'Last activity', 'Expires', 'Sent by']}
        rows={a.list.map(r => [<Badge tone={r.status === 'PartiallySigned' ? 'warn' : 'info'}>{r.status}</Badge>, <Link href={r.url}>{r.business || '(no name yet)'}</Link>, r.fields, r.docs, `${r.signed_owners}/${r.owners}`, fmtDate(r.last_activity), fmtDay(r.expires_at), r.sent_by || '—'])} />
    </div>
  );
}

// ── bits ──
function Section({ title, tone, children }) { return <section className="card p-4"><h3 className={`font-medium mb-2 ${tone === 'warn' ? 'text-amber-800' : ''}`}>{title}</h3>{children}</section>; }
function Stat({ label, v, sub }) { return <div className="card p-4"><div className="text-xs uppercase tracking-wide text-slate-500">{label}</div><div className="mt-1 text-2xl font-semibold">{v}</div>{sub && <div className="text-xs text-slate-500">{sub}</div>}</div>; }
function Link({ href, children }) { return <a href={href} target="_blank" rel="noreferrer" className="text-brand-700 hover:underline">{children}</a>; }
function Badge({ tone, children }) { const c = tone === 'ok' ? 'bg-emerald-50 text-emerald-800 ring-emerald-200' : tone === 'warn' ? 'bg-amber-50 text-amber-800 ring-amber-200' : 'bg-slate-100 text-slate-700 ring-slate-200'; return <span className={`inline-block rounded-full px-2 py-0.5 text-xs ring-1 ${c}`}>{children}</span>; }
function Docs({ r }) { return r.bank ? <Badge tone="ok">{r.bank} bank stmt</Badge> : r.docs ? <Badge tone="warn">{r.docs} doc, no bank</Badge> : <Badge tone="warn">no docs</Badge>; }
function Table({ cols, rows }) {
  if (!rows.length) return <p className="text-sm text-slate-500">None.</p>;
  return (
    <div className="overflow-x-auto">
      <table className="min-w-full text-sm">
        <thead><tr className="text-left text-xs uppercase tracking-wide text-slate-500 border-b border-slate-200">{cols.map(c => <th key={c} className="py-2 pr-4 font-medium">{c}</th>)}</tr></thead>
        <tbody>{rows.map((r, i) => <tr key={i} className="border-b border-slate-100 hover:bg-slate-50">{r.map((c, j) => <td key={j} className="py-1.5 pr-4 align-top whitespace-nowrap">{c}</td>)}</tr>)}</tbody>
      </table>
    </div>
  );
}
function toCsv(rows) {
  const cols = ['rep', 'business', 'completed', 'days', 'bank', 'docs', 'gross_monthly_sales', 'amount_requested', 'nudges', 'last_nudged', 'sent_by', 'lead_status', 'url'];
  const esc = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`;
  return [cols.join(','), ...rows.map(r => cols.map(c => esc(c === 'completed' ? String(r[c]).slice(0, 10) : r[c])).join(','))].join('\n');
}
